import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Upload, X, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  CurrencySelect,
  CURRENCY_NO_CONVERSION_NOTE,
} from '@/app/components/currency-select';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { useCreateExpense, useExpenseCategories, useUploadReceipt, useAddExpenseAllocation, useCostCodes } from '@/hooks/use-payroll';
import { usePersons } from '@/hooks/use-persons';
import { useProjects } from '@/hooks/use-projects';
import { usePlatformModules } from '@/hooks/use-modules';
import { useOrgCurrency, useOrgToday } from '@/hooks/use-settings';
import type { ExpensePaymentMethod, CreateExpenseAllocationPayload, Person } from '@/lib/types';

interface AllocationRow {
  project: string;
  amount: string;
  cost_code: string;
}

export function CreateExpensePage() {
  const navigate = useNavigate();
  const orgToday = useOrgToday();
  const orgCurrency = useOrgCurrency();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const createExpense = useCreateExpense();
  const uploadReceipt = useUploadReceipt();
  const addAllocation = useAddExpenseAllocation();
  const { isModuleEnabled } = usePlatformModules();
  const { data: categories } = useExpenseCategories({ is_active: true });
  const { data: personsData } = usePersons({ page_size: 100 });
  const { data: projects } = useProjects({ page_size: 100 }, isModuleEnabled('projects'));
  const { data: costCodes } = useCostCodes({ is_active: true });

  const [form, setForm] = useState({
    person: '',
    category: '',
    date: orgToday,
    description: '',
    amount: '',
    vendor: '',
    currency: '',
    is_billable: true,
    is_reimbursable: true,
    payment_method: 'personal_card' as ExpensePaymentMethod,
    notes: '',
  });

  const [files, setFiles] = useState<File[]>([]);
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected) {
      setFiles((prev) => [...prev, ...Array.from(selected)]);
    }
    e.target.value = '';
  }, []);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const addAllocationRow = () => {
    setAllocations((prev) => [...prev, { project: '', amount: '', cost_code: '' }]);
  };

  const updateAllocation = (index: number, field: keyof AllocationRow, value: string) => {
    setAllocations((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removeAllocation = (index: number) => {
    setAllocations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.person || !form.category || !form.amount || !form.description) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const expense = await createExpense.mutateAsync({
        person: Number(form.person),
        category: Number(form.category),
        date: form.date,
        description: form.description,
        amount: form.amount,
        vendor: form.vendor || undefined,
        currency: form.currency || undefined,
        is_billable: form.is_billable,
        is_reimbursable: form.is_reimbursable,
        payment_method: form.payment_method,
        notes: form.notes || undefined,
      });

      // Upload receipts
      for (const file of files) {
        await uploadReceipt.mutateAsync({ expenseId: expense.id, file });
      }

      // Add allocations
      for (const alloc of allocations) {
        if (alloc.project && alloc.amount) {
          const payload: CreateExpenseAllocationPayload = {
            project: Number(alloc.project),
            amount: alloc.amount,
            cost_code: alloc.cost_code || undefined,
          };
          await addAllocation.mutateAsync({ expenseId: expense.id, data: payload });
        }
      }

      navigate(`/payroll/expenses/${expense.id}`);
    } catch {
      setError('Failed to create expense. Please try again.');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/payroll/expenses">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">New Expense</h1>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Expense Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="person">Worker *</Label>
                <Select value={form.person} onValueChange={(v) => handleChange('person', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select worker" />
                  </SelectTrigger>
                  <SelectContent>
                    {personsData?.results?.map((p: Person) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.first_name} {p.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={form.category} onValueChange={(v) => handleChange('category', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.results?.map((cat) => (
                      <SelectItem key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="currency">Currency</Label>
                <CurrencySelect
                  id="currency"
                  value={form.currency || orgCurrency}
                  onValueChange={(value) => handleChange('currency', value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {CURRENCY_NO_CONVERSION_NOTE}
                </p>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="description">Description *</Label>
                <Input
                  placeholder="What was this expense for?"
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="vendor">Vendor</Label>
                <Input
                  placeholder="Store or vendor name"
                  value={form.vendor}
                  onChange={(e) => handleChange('vendor', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select
                  value={form.payment_method}
                  onValueChange={(v) => handleChange('payment_method', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal_card">Personal Card</SelectItem>
                    <SelectItem value="personal_cash">Personal Cash</SelectItem>
                    <SelectItem value="company_card">Company Card</SelectItem>
                    <SelectItem value="petty_cash">Petty Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_billable}
                  onChange={(e) => handleChange('is_billable', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm">Billable to client</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_reimbursable}
                  onChange={(e) => handleChange('is_reimbursable', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm">Reimbursable</span>
              </label>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                placeholder="Additional notes"
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Receipt Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Receipts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera className="h-4 w-4 mr-2" />
                Take Photo
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload File
              </Button>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileSelect}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFile(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Project Allocation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Project Allocation
              <Button type="button" variant="outline" size="sm" onClick={addAllocationRow}>
                <Plus className="h-4 w-4 mr-1" />
                Add Project
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {allocations.length === 0 ? (
              <p className="text-sm text-gray-500">
                No project allocation. Click &quot;Add Project&quot; to split this expense across projects.
              </p>
            ) : (
              <div className="space-y-3">
                {allocations.map((alloc, index) => (
                  <div key={index} className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label>Project</Label>
                      <Select
                        value={alloc.project}
                        onValueChange={(v) => updateAllocation(index, 'project', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects?.results?.map((proj) => (
                            <SelectItem key={proj.id} value={String(proj.id)}>
                              {proj.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-32">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={alloc.amount}
                        onChange={(e) => updateAllocation(index, 'amount', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="w-40">
                      <Label>Cost Code</Label>
                      <Select
                        value={alloc.cost_code || 'none'}
                        onValueChange={(v) => updateAllocation(index, 'cost_code', v === 'none' ? '' : v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Optional" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {costCodes?.map((cc) => (
                            <SelectItem key={cc.id} value={String(cc.id)}>
                              {cc.code} - {cc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAllocation(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={createExpense.isPending}>
            {createExpense.isPending ? 'Creating...' : 'Create Expense'}
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link to="/payroll/expenses">Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
